import { useState, type FormEvent } from "react";
import { useAuth } from "../context/AuthContext";
import { useLang } from "../context/LangContext";
import { ApiError } from "../services/api";
import { navigate } from "../router";

function readNextParam(): string {
  const hash = window.location.hash.replace(/^#/, "");
  const qIndex = hash.indexOf("?");
  if (qIndex === -1) return "/";
  const params = new URLSearchParams(hash.slice(qIndex + 1));
  const next = params.get("next");
  return next && next.startsWith("/") ? next : "/";
}

export function LoginPage() {
  const { session, login } = useAuth();
  const { t } = useLang();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Already signed in — bounce to the requested page.
  if (session) {
    navigate(readNextParam());
    return null;
  }

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await login(email.trim(), password);
      navigate(readNextParam());
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.message);
      } else {
        setError(t.loginNetworkError);
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="auth-wrap">
      <div className="card auth-card">
        <h1 className="auth-title">{t.loginTitle}</h1>
        <p className="muted auth-subtitle">{t.loginSubtitle}</p>

        <form onSubmit={onSubmit} className="auth-form">
          <label className="field">
            <span className="field-label">{t.loginEmail}</span>
            <input
              type="email"
              autoComplete="username"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              autoFocus
              placeholder="you@company.com"
            />
          </label>

          <label className="field">
            <span className="field-label">{t.loginPassword}</span>
            <input
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              placeholder="••••••••"
            />
          </label>

          {error && <div className="form-error">{error}</div>}

          <button
            type="submit"
            className="btn btn-primary btn-block"
            disabled={submitting}
          >
            {submitting ? t.loginSubmitting : t.loginSubmit}
          </button>
        </form>

        <p className="muted auth-foot">
          {t.loginFootBefore}
          <a href="https://survantic.com" target="_blank" rel="noreferrer">
            {t.loginFootLink}
          </a>
          {t.loginFootAfter}
        </p>
      </div>
    </div>
  );
}
